import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';

export function LivePulseStrip({
  liveGads, liveMeta, liveGa4,
  liveIkas, liveShopify, liveWoo,
  hasIkasConnected, hasShopifyConnected, hasWooConnected,
  isLoading, updatedAt, onRefresh,
}) {
  // Aggregate store revenue
  const storeRevenue = (liveIkas?.revenue || 0) + (liveShopify?.revenue || 0) + (liveWoo?.revenue || 0);
  const storeOrders = (liveIkas?.orders || 0) + (liveShopify?.orders || 0) + (liveWoo?.orders || 0);
  const hasStore = hasIkasConnected || hasShopifyConnected || hasWooConnected;

  const segments = [
    liveGads && { label: 'G.Ads', value: formatCurrencyCompact(liveGads.cost), sub: `${formatStat(liveGads.clicks)} clicks` },
    liveMeta && { label: 'Meta', value: formatCurrencyCompact(liveMeta.spend), sub: `${formatStat(liveMeta.clicks)} clicks` },
    liveGa4 && { label: 'GA4', value: formatStat(liveGa4.sessions), sub: `${formatStat(liveGa4.users)} users` },
    hasStore && storeRevenue > 0 && { label: 'Store', value: formatCurrencyCompact(storeRevenue), sub: `${formatStat(storeOrders)} orders` },
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.4 }}
      className="glass-card-live rounded-2xl px-4 py-2.5 mb-6"
    >
      <div className="flex items-center gap-3 overflow-x-auto">
        {/* Live dot + label */}
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="live-dot" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">LIVE</span>
        </div>

        <div className="h-4 w-px bg-gray-200 shrink-0" />

        {/* Platform segments */}
        {segments.map((seg, i) => (
          <React.Fragment key={seg.label}>
            {i > 0 && <div className="h-4 w-px bg-gray-200 shrink-0" />}
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] font-semibold text-gray-400 uppercase">{seg.label}</span>
              <span className="text-xs font-bold text-gray-800">{seg.value}</span>
              <span className="text-[10px] text-gray-400">{seg.sub}</span>
            </div>
          </React.Fragment>
        ))}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Timestamp + refresh */}
        <div className="flex items-center gap-2 shrink-0">
          {updatedAt && (
            <span className="text-[10px] text-gray-400">
              {updatedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={`text-gray-400 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
